'use client';

import { useState } from 'react';

interface InlineNewsletterSignupProps {
  title?: string;
  description?: string;
  variant?: 'default' | 'compact';
}

export default function InlineNewsletterSignup({
  title = "Want more insights like this?",
  description = "Join thousands of CTOs and technical leaders getting weekly insights on leadership and system design.",
  variant = 'default'
}: InlineNewsletterSignupProps) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      setMessage('Please enter your email');
      setStatus('error');
      return;
    }

    setStatus('loading');

    try {
      const response = await fetch('/api/public/newsletter', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        setStatus('success');
        setMessage('Check your email to confirm your subscription!');
        setEmail('');
      } else {
        setStatus('error');
        setMessage(data.error || 'Failed to subscribe');
      }
    } catch (error) {
      setStatus('error');
      setMessage('An error occurred. Please try again.');
    }
  };

  if (variant === 'compact') {
    return (
      <div className="my-8 p-4 bg-accent-muted border border-border rounded-lg">
        <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            disabled={status === 'loading' || status === 'success'}
            className="flex-1 px-4 py-2 bg-background border border-border rounded text-foreground placeholder-foreground-muted focus:outline-none focus:border-accent disabled:opacity-50"
          />
          <button
            type="submit"
            disabled={status === 'loading' || status === 'success'}
            className="px-6 py-2 bg-accent hover:bg-accent-hover text-accent-foreground font-semibold rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {status === 'loading' ? 'Subscribing...' : status === 'success' ? '✓ Subscribed' : 'Subscribe'}
          </button>
        </form>
        {message && (
          <p className={`mt-2 text-sm ${status === 'error' ? 'text-red-400' : 'text-green-400'}`}>
            {message}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="my-12 p-8 bg-gradient-to-r from-surface to-accent/5 border border-border rounded-lg">
      <div className="max-w-2xl mx-auto text-center">
        <h3 className="text-2xl font-bold text-foreground mb-3">{title}</h3>
        <p className="text-foreground-secondary mb-6">{description}</p>

        <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            disabled={status === 'loading' || status === 'success'}
            className="flex-1 px-4 py-3 bg-background border border-border rounded text-foreground placeholder-foreground-muted focus:outline-none focus:border-accent disabled:opacity-50"
          />
          <button
            type="submit"
            disabled={status === 'loading' || status === 'success'}
            className="px-8 py-3 bg-accent hover:bg-accent-hover text-accent-foreground font-semibold rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {status === 'loading' ? 'Subscribing...' : status === 'success' ? '✓ Subscribed' : 'Subscribe'}
          </button>
        </form>

        {message && (
          <p className={`mt-4 text-sm ${status === 'error' ? 'text-red-400' : 'text-green-400'}`}>
            {message}
          </p>
        )}

        <p className="mt-4 text-xs text-foreground-tertiary">
          No spam. Unsubscribe anytime.
        </p>
      </div>
    </div>
  );
}
